import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { Suspense, use } from "react";
import { DocsLayout } from "fumadocs-ui/layouts/docs";
import { DocsBody, DocsDescription, DocsPage, DocsTitle } from "fumadocs-ui/layouts/docs/page";
import { RootProvider } from "fumadocs-ui/provider/tanstack";
import { useFumadocsLoader } from "fumadocs-core/source/client";
import defaultMdxComponents from "fumadocs-ui/mdx";
import { docs, source } from "@/lib/docs-source";
import { ArchitectureDiagram } from "@/components/docs/architecture-diagram";
import { StorageDiagram } from "@/components/docs/storage-diagram";
import { PipelineDiagram } from "@/routes/stores/-components/pipeline-diagram";

export const Route = createFileRoute("/docs/$")({
  component: Page,
  loader: async ({ params }) => {
    const slugs = params["_splat"]?.split("/") ?? [];
    const data = await serverLoader({ data: slugs });
    await docs.getPage(data.path)?.preload();
    return data;
  },
});

const serverLoader = createServerFn({ method: "GET" })
  .validator((slugs: string[]) => slugs)
  .handler(async ({ data: slugs }) => {
    const page = source.getPage(slugs);
    if (!page) throw notFound();

    return {
      path: page.path,
      pageTree: await source.serializePageTree(source.getPageTree()),
    };
  });

// Custom components usable from any MDX page without imports.
const mdxComponents = {
  ...defaultMdxComponents,
  ArchitectureDiagram,
  PipelineDiagram,
  StorageDiagram,
};

function Content({ path }: { path: string }) {
  const page = docs.getPage(path);
  if (!page) throw new Error(`unknown page: ${path}`);

  const { toc } = use(page.load());
  const MDX = page.body;

  return (
    <DocsPage toc={toc}>
      <DocsTitle>{page.title}</DocsTitle>
      <DocsDescription>{page.description}</DocsDescription>
      <DocsBody>
        <MDX components={mdxComponents} />
      </DocsBody>
    </DocsPage>
  );
}

function Page() {
  const data = useFumadocsLoader(Route.useLoaderData());

  // The app's next-themes provider owns the .dark class, so fumadocs theme
  // handling stays off.
  return (
    <RootProvider theme={{ enabled: false }}>
      <DocsLayout nav={{ title: "BibaCV" }} tree={data.pageTree}>
        <Suspense>
          <Content path={data.path} />
        </Suspense>
      </DocsLayout>
    </RootProvider>
  );
}
